// client/src/components/pages/TelegramBI/BotManager/index.tsx (converted from index.js, ST-00220)
//
// DAQ-11 / ST-00203: the command reference now lists exactly the commands the bot answers
// (./botCommands). Changing a chat's role/authorization needs telegram.manage on the server
// (PUT /api/telegram/users/:chatId/permissions); the controls are read-only without it, and a
// refused change shows the server's message and leaves the row as it was.
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  type ComponentType,
  type ReactNode,
} from 'react';
import usePollingJs from '../../../../hooks/usePolling';
import {
  FaTelegramPlane,
  FaRobot,
  FaPaperPlane,
  FaUsers,
  FaComments,
  FaCheck,
  FaTimes,
  FaSync,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaChevronLeft,
  FaChevronRight,
  FaCopy,
  FaTerminal,
  FaHashtag,
  FaBullhorn,
  FaUser,
  FaUserFriends,
  FaShieldAlt,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaInfoCircle,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../../services/api';
import { PageHeader, ThemedSelect, useAuth } from '../../../../types/commonComponents';
import type { HttpErrorLike } from '../../../../types/clientShell';
import type {
  BotChatRow,
  BotChatsResponse,
  BotInfoResponse,
  BotStatsRaw,
  BotStatsState,
  BotStatusState,
  ChatPermissionUpdate,
  ChatsPagination,
  CountValue,
  TelegramBotIdentity,
  TestMessageResult,
} from '../../../../types/telegramBotManager';
import { BOT_COMMANDS } from './botCommands';

// Typed view of the (still JavaScript) polling hook.
const usePolling = usePollingJs as unknown as (
  callback: () => void,
  intervalMs: number,
  options?: { enabled?: boolean; immediate?: boolean }
) => void;

/** The API's own words for a failed request (`message`, then `error`), else the fallback. */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const data = (error as HttpErrorLike | null | undefined)?.response?.data;
  return data?.message || data?.error || fallback;
}

/** What the page shows before the bot status has loaded (renders like the old empty object). */
const NO_WEBHOOK: BotStatusState['webhook'] = {
  isSet: false,
  url: '',
  lastErrorDate: null,
  lastErrorMessage: null,
  pendingUpdateCount: 0,
  maxConnections: 0,
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const StatusDot = ({ online }: { online: boolean }) => (
  <span className="relative flex h-2.5 w-2.5">
    {online && (
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
    )}
    <span
      className={`relative inline-flex rounded-full h-2.5 w-2.5 ${online ? 'bg-emerald-500' : 'bg-red-500'}`}
    />
  </span>
);

interface MetricCardProps {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  icon: ComponentType<{ className?: string }>;
  accent?: string;
}

const MetricCard = ({ label, value, sub, icon: Icon, accent }: MetricCardProps) => (
  <div className="bg-surface rounded-lg border border-gray-200 p-4 hover:border-gray-300 transition-colors">
    <div className="flex items-start justify-between mb-3">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</span>
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent || 'bg-gray-100 text-gray-500'}`}
      >
        <Icon className="text-sm" />
      </div>
    </div>
    <div className="text-2xl font-semibold text-gray-900 tracking-tight">{value}</div>
    {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
  </div>
);

const Skeleton = ({ className }: { className?: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className || ''}`} />
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const BotManager = () => {
  const { hasPermission } = useAuth();
  // The server requires telegram.manage to change a chat's access (routes/telegram/users) and
  // to send a message as the bot (routes/telegramBot).
  const canManageChats = hasPermission('telegram.manage');

  // State ------------------------------------------------------------------
  const [botStatus, setBotStatus] = useState<BotStatusState | null>(null);
  const [stats, setStats] = useState<BotStatsState | null>(null);
  const [chats, setChats] = useState<BotChatRow[]>([]);
  const [chatsPagination, setChatsPagination] = useState<ChatsPagination>({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [chatSearch, setChatSearch] = useState('');

  const [loading, setLoading] = useState(true);
  const [chatsLoading, setChatsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Test message state
  const [testTarget, setTestTarget] = useState<BotChatRow | null>(null); // chat object or null
  const [testMessage, setTestMessage] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState<TestMessageResult | null>(null);

  // Commands section
  const [commandsExpanded, setCommandsExpanded] = useState(false);

  // Permission management
  const [permissionUpdating, setPermissionUpdating] = useState<BotChatRow['chat_id'] | null>(null);

  // Refs
  const testPanelRef = useRef<HTMLDivElement | null>(null);

  // Bot commands reference data (what the bot actually answers) ------------
  const botCommands = BOT_COMMANDS;

  // Data loading -----------------------------------------------------------

  const loadBotStatus = useCallback(async () => {
    try {
      const response = await api.telegramBI.bot.getStatus();
      const data = response.data as BotInfoResponse | undefined;
      const botData = data?.data || data;
      setBotStatus({
        isConnected: data?.success && botData?.bot ? true : false,
        bot: botData?.bot || {},
        webhook: {
          isSet: !!(botData?.webhook?.url && botData.webhook.url.length > 0),
          url: botData?.webhook?.url || '',
          lastErrorDate: botData?.webhook?.last_error_date || null,
          lastErrorMessage: botData?.webhook?.last_error_message || null,
          pendingUpdateCount: botData?.webhook?.pending_update_count || 0,
          maxConnections: botData?.webhook?.max_connections || 0,
        },
      });
    } catch (error) {
      console.error('Error loading bot status:', error);
      setBotStatus({
        isConnected: false,
        bot: {},
        webhook: {
          isSet: false,
          url: '',
          lastErrorDate: null,
          lastErrorMessage: null,
          pendingUpdateCount: 0,
          maxConnections: 0,
        },
      });
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const response = await api.telegramBI.bot.getStats();
      const body = response.data as { data?: BotStatsRaw } | BotStatsRaw | undefined;
      const raw: BotStatsRaw =
        (body && 'data' in body ? (body as { data?: BotStatsRaw }).data : undefined) ||
        (body as BotStatsRaw | undefined) ||
        {};
      const totalMessages: CountValue = raw.messages?.total_messages || raw.messagesTotal || 0;
      const sentMessages: CountValue = raw.messages?.sent_messages || 0;
      setStats({
        totalChats: raw.users?.active_users || raw.totalChats || 0,
        activeChats: raw.users?.active_users || raw.activeChats || 0,
        messagesToday: raw.messages?.MessagesToday || raw.messagesToday || 0,
        messagesTotal: totalMessages,
        deliveryRate:
          Number(totalMessages) > 0
            ? (Number(sentMessages) / Number(totalMessages)) * 100
            : raw.deliveryRate || null,
        commandsToday: raw.messages?.CommandsToday || raw.commandsToday || 0,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
      setStats({
        totalChats: 0,
        activeChats: 0,
        messagesToday: 0,
        messagesTotal: 0,
        deliveryRate: null,
        commandsToday: 0,
      });
    }
  }, []);

  const loadChats = useCallback(
    async (page = 1, search = '') => {
      try {
        setChatsLoading(true);
        const params: { page: number; limit: number; search?: string } = {
          page,
          limit: chatsPagination.limit,
        };
        if (search.trim()) {
          params.search = search.trim();
        }
        const response = await api.telegramBI.bot.getChats(params);
        const body = response.data as BotChatsResponse | undefined;
        const rawData = body?.data || body || {};
        const chatList = rawData?.chats || [];
        const total = rawData?.totalCount || rawData?.total || chatList.length;
        const totalPages = rawData?.totalPages || Math.ceil(total / chatsPagination.limit) || 1;

        setChats(chatList);
        setChatsPagination((prev) => ({
          ...prev,
          page,
          total,
          totalPages,
        }));
      } catch (error) {
        console.error('Error loading chats:', error);
        // One message per failing endpoint, however often search / paging reloads it (ST-00259).
        toast.error('Failed to load connected chats', { toastId: 'telegram-chats-load' });
        setChats([]);
      } finally {
        setChatsLoading(false);
      }
    },
    [chatsPagination.limit]
  );

  const loadAll = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      await Promise.allSettled([loadBotStatus(), loadStats(), loadChats(1, chatSearch)]);

      setLoading(false);
      setRefreshing(false);
    },
    [loadBotStatus, loadStats, loadChats, chatSearch]
  );

  // Initial load + auto-refresh every 30 seconds --------------------------
  useEffect(() => {
    loadAll(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Paused while this tab is in the background; refreshes on return.
  usePolling(
    useCallback(() => {
      loadBotStatus();
      loadStats();
    }, [loadBotStatus, loadStats]),
    30000
  );

  // Chat search debounce ---------------------------------------------------
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const handleChatSearchChange = (value: string) => {
    setChatSearch(value);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      loadChats(1, value);
    }, 400);
  };

  // Chat pagination --------------------------------------------------------
  const handleChatPageChange = (newPage: number) => {
    if (newPage < 1 || newPage > chatsPagination.totalPages) return;
    loadChats(newPage, chatSearch);
  };

  // Chat helpers -----------------------------------------------------------
  const getChatTypeLabel = (type: string | null | undefined): string => {
    switch (type) {
      case 'private':
        return 'Private';
      case 'group':
        return 'Group';
      case 'supergroup':
        return 'Supergroup';
      case 'channel':
        return 'Channel';
      default:
        return type || 'Unknown';
    }
  };

  const getChatTypeIcon = (type: string | null | undefined) => {
    switch (type) {
      case 'channel':
        return <FaBullhorn className="text-[#0088cc]" />;
      case 'group':
      case 'supergroup':
        return <FaUserFriends className="text-emerald-600" />;
      case 'private':
        return <FaUser className="text-gray-500" />;
      default:
        return <FaTelegramPlane className="text-[#0088cc]" />;
    }
  };

  const getChatTypeBadgeColor = (type: string | null | undefined): string => {
    switch (type) {
      case 'channel':
        return 'bg-blue-50 text-[#0088cc]';
      case 'group':
      case 'supergroup':
        return 'bg-emerald-50 text-emerald-700';
      case 'private':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-gray-100 text-gray-500';
    }
  };

  const getRoleLabel = (role: string | null | undefined): string => {
    switch (role) {
      case 'admin':
        return 'Admin';
      case 'editor':
        return 'Editor';
      case 'viewer':
        return 'Viewer';
      case 'guest':
        return 'Guest';
      default:
        return role || '—';
    }
  };

  const getChatDisplayName = (chat: BotChatRow): string => {
    if (chat.title) return chat.title;
    if (chat.username) return `@${chat.username}`;
    if (chat.first_name && chat.last_name) return `${chat.first_name} ${chat.last_name}`;
    if (chat.first_name) return chat.first_name;
    return `Chat ${chat.chat_id}`;
  };

  const formatLastActivity = (dateStr: string | null | undefined): string => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const copyChatId = (chatId: BotChatRow['chat_id']) => {
    navigator.clipboard
      .writeText(String(chatId))
      .then(() => {
        toast.success('Chat ID copied to clipboard');
      })
      .catch(() => {
        toast.error('Failed to copy');
      });
  };

  // Test message -----------------------------------------------------------
  const openTestPanel = (chat: BotChatRow) => {
    setTestTarget(chat);
    setTestMessage('');
    setTestResult(null);
    setTimeout(() => {
      testPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  const closeTestPanel = () => {
    setTestTarget(null);
    setTestMessage('');
    setTestResult(null);
  };

  const handleSendTestMessage = async () => {
    if (!testTarget || !testMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    try {
      setSendingTest(true);
      setTestResult(null);
      await api.telegramBI.bot.sendTestMessage(testTarget.chat_id, testMessage.trim());
      setTestResult({ success: true, message: 'Message delivered successfully' });
      toast.success('Test message sent');
      setTestMessage('');
    } catch (error) {
      const errMsg = apiErrorMessage(error, 'Failed to send message');
      setTestResult({ success: false, message: errMsg });
      toast.error(errMsg);
    } finally {
      setSendingTest(false);
    }
  };

  // The row changes only after the server accepted the change; a refusal (e.g. 403 without
  // telegram.manage) shows the server's message and leaves the row as it was.
  const updateChatPermissions = async (
    chatId: BotChatRow['chat_id'],
    permissions: ChatPermissionUpdate
  ) => {
    setPermissionUpdating(chatId);
    try {
      await api.telegramBI.users.updatePermissions(chatId, permissions);
      setChats((prev) =>
        prev.map((c) =>
          c.chat_id === chatId
            ? {
                ...c,
                role: permissions.role !== undefined ? permissions.role : c.role,
                is_authorized:
                  permissions.isActive !== undefined ? permissions.isActive : c.is_authorized,
                can_execute_queries:
                  permissions.canExecuteQueries !== undefined
                    ? permissions.canExecuteQueries
                    : c.can_execute_queries,
                can_manage_schedules:
                  permissions.canManageSchedules !== undefined
                    ? permissions.canManageSchedules
                    : c.can_manage_schedules,
                can_manage_templates:
                  permissions.canManageTemplates !== undefined
                    ? permissions.canManageTemplates
                    : c.can_manage_templates,
              }
            : c
        )
      );
      toast.success('Permissions updated');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to update permissions'));
    } finally {
      setPermissionUpdating(null);
    }
  };

  // Derived ----------------------------------------------------------------
  const isConnected = botStatus?.isConnected || false;
  const bot: TelegramBotIdentity = botStatus?.bot || {};
  const webhook: BotStatusState['webhook'] = botStatus?.webhook || NO_WEBHOOK;
  const deliveryRateDisplay =
    stats?.deliveryRate != null ? `${stats.deliveryRate.toFixed(1)}%` : 'N/A';

  // Loading skeleton -------------------------------------------------------
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="bg-surface border-b border-slate-200">
          <div className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-3">
            <div className="animate-pulse flex items-center gap-4">
              <div className="hidden sm:block w-11 h-11 bg-gray-200 rounded-xl" />
              <div>
                <Skeleton className="h-6 w-48 mb-1" />
                <Skeleton className="h-4 w-64 hidden sm:block" />
              </div>
            </div>
          </div>
        </div>
        <div className="p-3 sm:p-6 max-w-[1800px] mx-auto animate-pulse space-y-6">
          <Skeleton className="h-36 w-full rounded-lg" />
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-28 rounded-lg" />
            ))}
          </div>
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        icon={FaRobot}
        title="Bot Manager"
        subtitle="Monitor and manage your Telegram bot connection"
        actions={
          <button
            type="button"
            onClick={() => loadAll(true)}
            disabled={refreshing}
            aria-label="Refresh"
            title="Refresh"
            className="inline-flex items-center justify-center gap-2 h-10 min-w-10 px-2.5 border border-slate-300 bg-surface text-slate-700 hover:border-slate-400 hover:bg-slate-50 rounded-lg text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50"
          >
            <FaSync
              className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
            <span className="page-header__label">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        }
      />

      <div className="p-3 sm:p-6 max-w-[1800px] mx-auto">
        {/* ================================================================ */}
        {/* Section 1: Bot Status Header                                      */}
        {/* ================================================================ */}
        <div className="bg-surface rounded-lg border border-gray-200 mb-6">
          <div className="p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              {/* Bot avatar area */}
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="relative flex-shrink-0">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0088cc] to-[#0077b5] flex items-center justify-center shadow-md">
                    <FaRobot className="text-white text-2xl" />
                  </div>
                  <div
                    className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full border-2 border-white flex items-center justify-center"
                    style={{ backgroundColor: isConnected ? '#10b981' : '#ef4444' }}
                  >
                    {isConnected ? (
                      <FaCheck className="text-white text-[8px]" />
                    ) : (
                      <FaTimes className="text-white text-[8px]" />
                    )}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold text-gray-900 truncate">
                      {bot.first_name || 'Telegram Bot'}
                    </h2>
                    <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-gray-200 bg-surface">
                      <StatusDot online={isConnected} />
                      <span
                        className={`text-xs font-medium ${isConnected ? 'text-emerald-700' : 'text-red-600'}`}
                      >
                        {isConnected ? 'Online' : 'Offline'}
                      </span>
                    </div>
                  </div>
                  {bot.username && (
                    <p className="text-sm text-[#0088cc] font-medium mt-0.5">@{bot.username}</p>
                  )}
                  {bot.id && <p className="text-xs text-gray-400 mt-0.5">Bot ID: {bot.id}</p>}
                </div>
              </div>

              {/* Webhook info */}
              <div className="sm:text-right flex-shrink-0">
                <div className="flex items-center gap-2 sm:justify-end">
                  {webhook.isSet ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <FaCheckCircle className="text-[10px]" />
                      Webhook Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                      <FaTimesCircle className="text-[10px]" />
                      Webhook Not Set
                    </span>
                  )}
                </div>
                {webhook.url && (
                  <p
                    className="text-xs text-gray-400 mt-1.5 truncate max-w-xs sm:max-w-sm"
                    title={webhook.url}
                  >
                    {webhook.url}
                  </p>
                )}
                {webhook.pendingUpdateCount > 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    {webhook.pendingUpdateCount} pending update
                    {webhook.pendingUpdateCount !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>

            {/* Webhook error banner */}
            {webhook.lastErrorDate && webhook.lastErrorMessage && (
              <div className="mt-4 flex items-start gap-3 p-3 rounded-lg bg-red-50 border border-red-200">
                <FaExclamationTriangle className="text-red-500 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-red-800 font-medium">Webhook Error</p>
                  <p className="text-xs text-red-700 mt-0.5">{webhook.lastErrorMessage}</p>
                  <p className="text-xs text-red-500 mt-0.5">
                    {new Date(webhook.lastErrorDate * 1000).toLocaleString()}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ================================================================ */}
        {/* Section 2: Stats Overview                                         */}
        {/* ================================================================ */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <MetricCard
            label="Total Chats"
            value={stats?.totalChats ?? '--'}
            sub="All connected chats"
            icon={FaUsers}
            accent="bg-blue-50 text-[#0088cc]"
          />
          <MetricCard
            label="Active Chats"
            value={stats?.activeChats ?? '--'}
            sub="Currently active"
            icon={FaComments}
            accent="bg-emerald-50 text-emerald-600"
          />
          <MetricCard
            label="Messages Today"
            value={stats?.messagesToday ?? '--'}
            sub={
              stats?.messagesTotal ? `${Number(stats.messagesTotal).toLocaleString()} total` : null
            }
            icon={FaPaperPlane}
            accent="bg-[#0088cc]/10 text-[#0088cc]"
          />
          <MetricCard
            label="Messages Total"
            value={
              stats?.messagesTotal != null ? Number(stats.messagesTotal).toLocaleString() : '--'
            }
            sub={stats?.commandsToday ? `${stats.commandsToday} commands today` : null}
            icon={FaHashtag}
            accent="bg-violet-50 text-violet-600"
          />
          <MetricCard
            label="Delivery Rate"
            value={deliveryRateDisplay}
            sub="Success rate"
            icon={FaCheckCircle}
            accent="bg-amber-50 text-amber-600"
          />
        </div>

        {/* ================================================================ */}
        {/* Section 3: Connected Chats                                        */}
        {/* ================================================================ */}
        <div className="bg-surface rounded-lg border border-gray-200 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between px-4 py-3 border-b border-gray-100 gap-3">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <FaUsers className="text-[#0088cc] text-xs" />
              Connected Chats
              {chatsPagination.total > 0 && (
                <span className="text-xs font-normal text-gray-400">({chatsPagination.total})</span>
              )}
            </h2>
            <div className="relative w-full sm:w-64">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
              <input
                type="text"
                placeholder="Search chats..."
                value={chatSearch}
                onChange={(e) => handleChatSearchChange(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-surface focus:ring-2 focus:ring-[#0088cc]/20 focus:border-[#0088cc] outline-none transition-all"
              />
            </div>
          </div>

          {/* Chat table */}
          {chatsLoading ? (
            <div className="p-8 flex items-center justify-center">
              <div className="flex items-center gap-3 text-gray-500">
                <div className="w-5 h-5 border-2 border-gray-300 border-t-[#0088cc] rounded-full animate-spin" />
                <span className="text-sm">Loading chats...</span>
              </div>
            </div>
          ) : chats.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <FaUsers className="mx-auto text-3xl text-gray-300 mb-3" />
              <p className="text-sm font-medium text-gray-600 mb-1">
                {chatSearch ? 'No chats match your search' : 'No connected chats'}
              </p>
              <p className="text-xs text-gray-400 max-w-sm mx-auto">
                {chatSearch
                  ? 'Try a different search term.'
                  : 'Your bot needs to receive at least one message in a chat to register it. Send /start or /help in a group where the bot is a member.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left px-4 py-2.5 text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                      Chat ID
                    </th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                      Name / Title
                    </th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                      Auth
                    </th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                      Last Activity
                    </th>
                    <th className="text-right px-4 py-2.5 text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {chats.map((chat) => {
                    const isTestTarget = testTarget?.chat_id === chat.chat_id;
                    return (
                      <tr
                        key={chat.chat_id}
                        className={`hover:bg-gray-50/50 transition-colors ${isTestTarget ? 'bg-blue-50/40' : ''}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <code className="text-xs font-mono text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded">
                              {chat.chat_id}
                            </code>
                            <button
                              onClick={() => copyChatId(chat.chat_id)}
                              className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                              title="Copy Chat ID"
                            >
                              <FaCopy className="text-[10px]" />
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 min-w-0">
                            {getChatTypeIcon(chat.chat_type)}
                            <div className="min-w-0">
                              <span className="text-sm font-medium text-gray-900 truncate block max-w-[200px]">
                                {getChatDisplayName(chat)}
                              </span>
                              {chat.username && (
                                <span className="text-xs text-gray-400 block">
                                  @{chat.username}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${getChatTypeBadgeColor(chat.chat_type)}`}
                          >
                            {getChatTypeLabel(chat.chat_type)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {permissionUpdating === chat.chat_id ? (
                            <div className="w-20 h-6 bg-gray-100 rounded animate-pulse" />
                          ) : !canManageChats ? (
                            <span className="text-xs text-gray-600">{getRoleLabel(chat.role)}</span>
                          ) : (
                            <ThemedSelect
                              value={chat.role || ''}
                              onChange={(e) => {
                                const newRole = e.target.value;
                                if (newRole) {
                                  updateChatPermissions(chat.chat_id, {
                                    role: newRole,
                                    isActive: true,
                                    canExecuteQueries: true,
                                  });
                                }
                              }}
                              className="h-7 text-xs min-w-[90px]"
                            >
                              <option value="">—</option>
                              <option value="admin">Admin</option>
                              <option value="editor">Editor</option>
                              <option value="viewer">Viewer</option>
                              <option value="guest">Guest</option>
                            </ThemedSelect>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {permissionUpdating === chat.chat_id ? (
                            <div className="w-16 h-6 bg-gray-100 rounded animate-pulse" />
                          ) : chat.is_authorized ? (
                            <div className="flex items-center gap-1.5">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                Auth'd
                              </span>
                              {canManageChats && (
                                <button
                                  onClick={() =>
                                    updateChatPermissions(chat.chat_id, { isActive: false })
                                  }
                                  className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                  title="Revoke authorization"
                                >
                                  <FaTimesCircle className="text-[10px]" />
                                </button>
                              )}
                            </div>
                          ) : !canManageChats ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-500">
                              Not authorized
                            </span>
                          ) : (
                            <button
                              onClick={() =>
                                updateChatPermissions(chat.chat_id, {
                                  role: 'viewer',
                                  isActive: true,
                                  canExecuteQueries: true,
                                })
                              }
                              className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
                            >
                              <FaShieldAlt className="text-[9px]" />
                              Authorize
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {formatLastActivity(chat.last_activity)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {/* Sending as the bot needs telegram.manage (routes/telegramBot). */}
                          {canManageChats && (
                            <button
                              onClick={() =>
                                isTestTarget ? closeTestPanel() : openTestPanel(chat)
                              }
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                isTestTarget
                                  ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                  : 'bg-[#0088cc] text-white hover:bg-[#0077b5]'
                              }`}
                            >
                              {isTestTarget ? (
                                <>
                                  <FaTimes className="text-[9px]" />
                                  Cancel
                                </>
                              ) : (
                                <>
                                  <FaPaperPlane className="text-[9px]" />
                                  Send Test
                                </>
                              )}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {chatsPagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100">
              <span className="text-xs text-gray-500">
                Page {chatsPagination.page} of {chatsPagination.totalPages}
                {chatsPagination.total > 0 && (
                  <span className="ml-2 text-gray-400">({chatsPagination.total} total)</span>
                )}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleChatPageChange(chatsPagination.page - 1)}
                  disabled={chatsPagination.page <= 1}
                  className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-30 transition-colors"
                >
                  <FaChevronLeft className="text-xs" />
                </button>
                <button
                  onClick={() => handleChatPageChange(chatsPagination.page + 1)}
                  disabled={chatsPagination.page >= chatsPagination.totalPages}
                  className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-30 transition-colors"
                >
                  <FaChevronRight className="text-xs" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ================================================================ */}
        {/* Section 4: Test Message Panel (inline, below chat table)          */}
        {/* ================================================================ */}
        {testTarget && (
          <div
            ref={testPanelRef}
            className="bg-surface rounded-lg border border-[#0088cc]/30 mb-6 shadow-sm shadow-[#0088cc]/5"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <FaPaperPlane className="text-[#0088cc] text-xs" />
                Send Test Message
              </h3>
              <button
                onClick={closeTestPanel}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <FaTimes className="text-xs" />
              </button>
            </div>
            <div className="p-4">
              {/* Target display */}
              <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-gray-50 border border-gray-200">
                <div className="w-8 h-8 rounded-lg bg-[#0088cc]/10 flex items-center justify-center flex-shrink-0">
                  {getChatTypeIcon(testTarget.chat_type)}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {getChatDisplayName(testTarget)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {getChatTypeLabel(testTarget.chat_type)} &middot; ID: {testTarget.chat_id}
                  </div>
                </div>
              </div>

              {/* Message input */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Message</label>
                <textarea
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  rows={3}
                  placeholder="Type your test message here... Supports Telegram markdown formatting."
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0088cc]/20 focus:border-[#0088cc] outline-none transition-all resize-none"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      handleSendTestMessage();
                    }
                  }}
                />
                <p className="text-[11px] text-gray-400 mt-1">Press Ctrl+Enter to send</p>
              </div>

              {/* Send button + result */}
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSendTestMessage}
                  disabled={sendingTest || !testMessage.trim()}
                  className="flex items-center gap-2 px-4 py-2 bg-[#0088cc] hover:bg-[#0077b5] text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendingTest ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane className="text-xs" />
                      Send Message
                    </>
                  )}
                </button>

                {testResult && (
                  <div
                    className={`flex items-center gap-2 text-sm ${testResult.success ? 'text-emerald-700' : 'text-red-600'}`}
                  >
                    {testResult.success ? (
                      <FaCheckCircle className="text-emerald-500" />
                    ) : (
                      <FaTimesCircle className="text-red-500" />
                    )}
                    <span className="text-xs">{testResult.message}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ================================================================ */}
        {/* Section 5: Bot Commands Reference                                 */}
        {/* ================================================================ */}
        <div className="bg-surface rounded-lg border border-gray-200">
          <button
            onClick={() => setCommandsExpanded(!commandsExpanded)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50/50 transition-colors"
          >
            <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <FaTerminal className="text-[#0088cc] text-xs" />
              Bot Commands Reference
              <span className="text-xs font-normal text-gray-400">
                ({botCommands.length} commands)
              </span>
            </h2>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">
                {commandsExpanded ? 'Collapse' : 'Expand'}
              </span>
              {commandsExpanded ? (
                <FaChevronUp className="text-xs text-gray-400" />
              ) : (
                <FaChevronDown className="text-xs text-gray-400" />
              )}
            </div>
          </button>

          {commandsExpanded && (
            <div className="border-t border-gray-100">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left px-4 py-2.5 text-[11px] font-medium text-gray-500 uppercase tracking-wider w-36">
                        Command
                      </th>
                      <th className="text-left px-4 py-2.5 text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="text-left px-4 py-2.5 text-[11px] font-medium text-gray-500 uppercase tracking-wider w-52">
                        Example
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {botCommands.map((cmd) => (
                      <tr key={cmd.command} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-2.5">
                          <code className="text-xs font-mono font-semibold text-[#0088cc] bg-blue-50 px-2 py-0.5 rounded">
                            {cmd.command}
                          </code>
                        </td>
                        <td className="px-4 py-2.5 text-sm text-gray-700">{cmd.description}</td>
                        <td className="px-4 py-2.5">
                          <code className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                            {cmd.example}
                          </code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50">
                <div className="flex items-start gap-2">
                  <FaInfoCircle className="text-gray-400 text-xs mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-500">
                    Users can send these commands in any chat where the bot is active. Every command
                    except /start and /help needs the chat to be authorized above first. Commands
                    are case-insensitive. The bot must be an admin in channels to receive and
                    process commands.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BotManager;
